import { type LabelColor, labelColors } from '../../tokens/colors';

/** The label colors autoColor may assign: everything except gray. */
export type AutoLabelColor = Exclude<LabelColor, 'gray'>;

/* Gray is excluded from the hash. It is the intentional mute, the color a
 * user picks on purpose to de-emphasise a label, and a hash that could
 * assign it would make one tag in eleven look muted by accident. */
const hashable = labelColors.filter((c): c is AutoLabelColor => c !== 'gray');

/**
 * The default color of a label with no stored override: a deterministic hash
 * of the text onto the eleven non-gray label colors. The same text always
 * hashes to the same hue, on every screen, in every session, with nothing
 * persisted; two surfaces rendering the same tag agree without coordinating.
 *
 * Only explicit user picks are stored. An unpicked color is a projection of
 * the text, and storing it would create a second copy that can drift.
 *
 * The function is part of the rendered product, so the algorithm is FROZEN:
 * changing it silently recolors every unstored tag everywhere. (djb2 over
 * UTF-16 code units, modulo eleven.)
 */
export function autoColor(text: string): AutoLabelColor {
  let hash = 5381;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 33 + text.charCodeAt(i)) >>> 0;
  }
  /* The modulo keeps the index in range; the assertion is for consumers
   * compiled with noUncheckedIndexedAccess. */
  return hashable[hash % hashable.length] as AutoLabelColor;
}
